
import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { SeoMetadata } from '@/types/seo';
import { useToast } from '@/hooks/use-toast';
import { syncEventCategoriesToSeo, cleanupInactiveEventSeoMetadata } from '@/services/seoEventSync';

export const useSeoMetadata = () => {
  const { toast } = useToast();

  const { data: seoData = [], isLoading: loading, refetch, error } = useQuery({
    queryKey: ['seo-metadata'],
    queryFn: async () => {
      console.log('useSeoMetadata - Fetching SEO data');
      
      // Synchroniser les évènements vers SEO avant de récupérer les données
      try {
        const syncedCount = await syncEventCategoriesToSeo();
        console.log(`✅ ${syncedCount} évènements synchronisés dans seo_metadata`);
        const deletedCount = await cleanupInactiveEventSeoMetadata();
        console.log(`🗑️ ${deletedCount || 0} pages obsolètes supprimées`);
      } catch (syncError) {
        console.error('Erreur lors de la synchronisation des évènements:', syncError);
        // Continue même en cas d'erreur de sync
      }
      
      // Récupérer toutes les métadonnées SEO (y compris les articles et évènements synchronisés automatiquement)
      const { data: seoMetadata, error: seoError } = await supabase
        .from('seo_metadata')
        .select('*')
        .order('page_path');

      if (seoError) {
        console.error('Erreur lors du chargement des métadonnées SEO:', seoError);
        throw seoError;
      }

      console.log('useSeoMetadata - SEO data fetched:', seoMetadata);
      // Cast page_type to the correct type
      return (seoMetadata || []).map(item => ({
        ...item,
        page_type: item.page_type as 'particulier' | 'professionnel' | 'other' | undefined
      })) as SeoMetadata[];
    },
    // Mise à jour automatique toutes les 5 minutes pour capturer les nouveaux articles et évènements
    refetchInterval: 5 * 60 * 1000,
    // Mise à jour quand la fenêtre reprend le focus
    refetchOnWindowFocus: true,
  });

  // Écouter les changements en temps réel sur seo_metadata pour mise à jour automatique
  useEffect(() => {
    const channel = supabase
      .channel('seo-metadata-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'seo_metadata'
        },
        (payload) => {
          console.log('SEO metadata modifié, mise à jour:', payload);
          // Recharger les données SEO quand les métadonnées sont modifiées
          refetch();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [refetch]);

  // Gérer l'erreur avec useEffect au lieu de onError
  useEffect(() => {
    if (error) {
      console.error('Erreur useQuery SEO metadata:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les métadonnées SEO",
        variant: "destructive"
      });
    }
  }, [error, toast]);

  const ensureEssentialPagesExist = async (existingData: SeoMetadata[]) => {
    const essentialPages = getEssentialPagesMetadata();
    const existingPaths = existingData.map(item => item.page_path);
    
    const missingPages = essentialPages.filter(page => 
      !existingPaths.includes(page.page_path)
    );

    if (missingPages.length > 0) {
      try {
        console.log('Création de pages SEO manquantes:', missingPages);
        
        // Utiliser upsert pour éviter les conflits de clés
        const { error } = await supabase
          .from('seo_metadata')
          .upsert(missingPages, { 
            onConflict: 'page_path',
            ignoreDuplicates: true 
          });

        if (error) {
          console.error('Erreur lors de la création des pages manquantes:', error);
          // Ne pas faire échouer la requête principale si les pages par défaut ne peuvent pas être créées
        } else {
          console.log(`${missingPages.length} pages SEO manquantes créées ou mises à jour`);
        }
      } catch (error) {
        console.error('Erreur lors de la création des pages SEO manquantes:', error);
      }
    }
  };

  return {
    seoData,
    loading,
    refetch
  };
};

export const useSeoMetadataForPage = (pagePath: string) => {
  const { data: metadata, isLoading: loading } = useQuery({
    queryKey: ['seo-metadata', pagePath],
    queryFn: async () => {
      console.log('useSeoMetadataForPage - Fetching for path:', pagePath);
      
      const { data, error } = await supabase
        .from('seo_metadata')
        .select('*')
        .eq('page_path', pagePath)
        .maybeSingle();

      if (error) {
        console.error('Erreur lors du chargement des métadonnées pour la page:', error);
        throw error;
      }

      console.log('useSeoMetadataForPage - Data found:', data);
      // Cast page_type to the correct type
      return data ? {
        ...data,
        page_type: data.page_type as 'particulier' | 'professionnel' | 'other' | undefined
      } as SeoMetadata : null;
    },
    enabled: !!pagePath
  });

  return { metadata, loading };
};

// Fonction pour définir les métadonnées essentielles de toutes les pages
export const getEssentialPagesMetadata = () => {
  return [
    {
      page_path: '/',
      title: 'Organisation d\'évènements en Île-de-France | Maison Eridia',
      meta_description: 'Maison Eridia, votre maison qui offre les services de l\'évènementiel dans toutes ses couleurs. Organisation complète d\'évènements privés et professionnels en Île-de-France : mariages, anniversaires, séminaires et team building, conférences, soirées d\'entreprise. Création sur mesure, coordination clé en main.',
      keywords: 'maison eridia, eridia, organisation évènements, évènementiel Île-de-France, agence évènementielle Paris, mariage sur-mesure, anniversaire signature, séminaire entreprise, créateurs digitaux',
      no_index: false
    },
    {
      page_path: '/home',
      title: 'Organisation d\'évènements en Île-de-France | Maison Eridia',
      meta_description: 'Maison Eridia, votre maison qui offre les services de l\'évènementiel dans toutes ses couleurs. Organisation complète d\'évènements privés et professionnels en Île-de-France : mariages, anniversaires, séminaires et team building, conférences, soirées d\'entreprise. Création sur mesure, coordination clé en main.',
      keywords: 'maison eridia, eridia, organisation évènements, évènementiel Île-de-France, agence évènementielle Paris, mariage sur-mesure, anniversaire signature, séminaire entreprise, créateurs digitaux',
      no_index: false
    },
    {
      page_path: '/univers-particuliers',
      title: 'Évènements privés sur mesure en IDF | Maison Eridia',
      meta_description: 'Maison Eridia organise vos évènements privés en Île-de-France : mariages sur-mesure, anniversaires inoubliables, demandes en mariage, Saint-Valentin. Votre évènement clé en main avec Eridia.',
      keywords: 'maison eridia, eridia, organisation évènement privé Paris, mariage Île-de-France, anniversaire chic, demande en mariage, évènement sur mesure, agence évènementielle',
      no_index: false
    },
    {
      page_path: '/professionnels',
      title: 'Agence évènementielle B2B à Paris | Maison Eridia',
      meta_description: 'Organisation d\'évènements professionnels à Paris et Île-de-France : séminaires et team building, conférences et évènements clients, soirées d\'entreprise et évènements créateurs digitaux. Devis gratuit sous 24h, coordination clé en main.',
      keywords: 'évènement professionnel Paris, agence évènementielle B2B, séminaire entreprise, team building Île-de-France, conférence entreprise, soirée corporate, évènement créateurs digitaux',
      no_index: false
    },
    {
      page_path: '/prospectus-particuliers',
      title: 'Prospectus évènements privés | Maison Eridia',
      meta_description: 'Recevez gratuitement le prospectus Maison Eridia pour vos évènements privés : mariages, anniversaires signature, demandes en mariage. Prestations et lieux d\'exception en Île-de-France.',
      keywords: 'prospectus mariage, brochure évènement privé, plaquette wedding planner, anniversaire prestige, Maison Eridia',
      no_index: false
    },
    {
      page_path: '/prospectus-professionnels',
      title: 'Prospectus évènements d\'entreprise | Maison Eridia',
      meta_description: 'Recevez gratuitement le prospectus Maison Eridia pour vos évènements d\'entreprise : séminaires, soirées corporate, galas, lancements. Direction artistique et logistique premium en Île-de-France.',
      keywords: 'prospectus évènement entreprise, brochure séminaire, plaquette agence évènementielle B2B, gala entreprise, Maison Eridia',
      no_index: false
    },
    {
      page_path: '/faq',
      title: 'FAQ | Organisation d\'évènements | Maison Eridia',
      meta_description: 'Toutes les réponses à vos questions sur nos prestations, tarifs, conditions et organisation d\'évènements.',
      keywords: 'faq évènements, questions fréquentes, organisation évènements',
      no_index: false
    },
    {
      page_path: '/a-propos',
      title: 'À propos | Maison Eridia – Votre partenaire évènementiel',
      meta_description: 'Découvrez l\'histoire, les valeurs et le savoir-faire de Maison Eridia, agence d\'organisation d\'évènements en Île-de-France.',
      keywords: 'à propos, Maison Eridia, agence évènementielle, histoire',
      no_index: false
    },
    {
      page_path: '/cgv',
      title: 'Conditions Générales de Vente | Maison Eridia',
      meta_description: 'Consultez les conditions générales de vente applicables à nos prestations évènementielles pour particuliers et entreprises.',
      keywords: 'conditions générales vente, CGV, prestations évènementielles',
      no_index: false
    },
    {
      page_path: '/contact',
      title: 'Contact | Agence évènementielle IDF | Maison Eridia',
      meta_description: 'Vous avez un projet d\'évènement ? Contactez notre équipe pour une étude personnalisée et un devis sur mesure.',
      keywords: 'contact évènements, nous contacter, devis évènement',
      no_index: false
    },
    {
      page_path: '/mentions-legales',
      title: 'Mentions légales | Maison Eridia',
      meta_description: 'Mentions légales et informations sur Maison Eridia, agence d\'organisation d\'évènements en Île-de-France.',
      keywords: 'mentions légales, informations légales',
      no_index: false
    },
    {
      page_path: '/nos-offres',
      title: 'Nos offres évènementielles | Maison Eridia',
      meta_description: 'Découvrez nos offres évènementielles premium en Île-de-France : organisation complète, décoration sur mesure, coordination pour évènements privés et professionnels.',
      keywords: 'offres évènement, packages évènementiels, formules organisation',
      no_index: false
    },
    {
      page_path: '/evenements-particuliers',
      title: 'Organisation évènements particuliers | Maison Eridia',
      meta_description: 'Spécialiste des évènements particuliers en Île-de-France : mariage sur-mesure, anniversaire signature, demande en mariage, Saint-Valentin.',
      keywords: 'évènements particuliers, organisation privée, célébrations',
      no_index: false
    },
    {
      page_path: '/evenements-professionnels',
      title: 'Évènements Professionnels Corporate Paris | Maison Eridia',
      meta_description: 'Agence spécialisée dans l\'organisation d\'évènements professionnels : séminaires et team building, conférences et évènements clients, soirées d\'entreprise et créateurs digitaux en Île-de-France.',
      keywords: 'évènements professionnels Paris, corporate event, agence évènementielle entreprise, organisation séminaire, soirée entreprise, conférence, créateurs digitaux',
      no_index: false
    },
    {
      page_path: '/organisation-evenements',
      title: 'Agence organisation évènements | Maison Eridia',
      meta_description: 'Agence spécialisée dans l\'organisation d\'évènements privés et professionnels en Île-de-France. Coordination complète, décoration sur mesure.',
      keywords: 'organisation évènements, coordination évènementielle, agence',
      no_index: false
    },
    {
      page_path: '/nos-evenements-professionnels',
      title: 'Solutions évènements professionnels | Maison Eridia',
      meta_description: 'Solutions évènements professionnels sur mesure : séminaires & team building, conférences & évènements clients, soirées d\'entreprise, créateurs & entrepreneurs digitaux.',
      keywords: 'solutions professionnelles, séminaires team building, soirées entreprise, conférences, créateurs digitaux, entrepreneurs',
      no_index: false
    },
    {
      page_path: '/blog',
      title: 'Blog évènementiel | Conseils d\'experts | Maison Eridia',
      meta_description: 'Découvrez le blog de Maison Eridia : conseils d\'experts, inspirations déco, tendances organisation pour mariages, anniversaires et évènements privés en Île-de-France. Eridia partage son savoir-faire.',
      keywords: 'blog maison eridia, conseils évènements, blog eridia, inspiration évènementielle, tendances mariage, organisation anniversaire',
      no_index: false
    },
    {
      page_path: '/devis',
      title: 'Devis gratuit évènements | Maison Eridia',
      meta_description: 'Obtenez votre devis personnalisé gratuit pour évènements privés et professionnels en Île-de-France. Organisation clé en main, réponse sous 24h.',
      keywords: 'devis évènement gratuit, devis personnalisé, tarif évènement',
      no_index: false
    },
    {
      page_path: '/demande-devis',
      title: 'Demande de devis pour évènements | Maison Eridia',
      meta_description: 'Formulaire de demande de devis personnalisé pour votre évènement privé ou professionnel en Île-de-France. Service sur mesure et réponse rapide.',
      keywords: 'demande devis, devis évènement personnalisé',
      no_index: false
    },
    {
      page_path: '/demande-de-devis-pour-evenements-professionnels',
      title: 'Devis Évènement Professionnel Gratuit | Maison Eridia',
      meta_description: 'Demandez votre devis gratuit pour séminaire, team building, conférence, soirée d\'entreprise ou évènement créateurs digitaux. Réponse sous 24h ouvrées.',
      keywords: 'devis évènement professionnel, tarif séminaire entreprise, prix team building, budget conférence, estimation soirée corporate, évènement créateurs digitaux',
      no_index: false
    },
    {
      page_path: '/politique-cookies',
      title: 'Politique de cookies | Maison Eridia',
      meta_description: 'Politique de gestion des cookies du site Maison Eridia, organisation d\'évènements privés en Île-de-France.',
      keywords: 'politique cookies, confidentialité',
      no_index: false
    },
    {
      page_path: '/politique-confidentialite',
      title: 'Politique de confidentialité | Maison Eridia',
      meta_description: 'Découvrez comment Maison Eridia protège vos données personnelles conformément au RGPD. Transparence et respect de votre vie privée.',
      keywords: 'politique confidentialité, RGPD, protection données',
      no_index: false
    },
    {
      page_path: '/cgu',
      title: 'Conditions générales d\'utilisation | Maison Eridia',
      meta_description: 'Conditions générales d\'utilisation du site Maison Eridia pour l\'organisation d\'évènements privés en Île-de-France.',
      keywords: 'conditions générales, CGU, utilisation site',
      no_index: false
    },
    {
      page_path: '/dashboard',
      title: 'Espace professionnel | Maison Eridia',
      meta_description: 'Accédez à votre tableau de bord Maison Eridia pour suivre vos évènements, devis et factures.',
      keywords: 'dashboard, espace pro, tableau de bord',
      no_index: true
    }
  ];
};

// Fonction utilitaire pour créer les métadonnées par défaut des sitelinks (maintenue pour compatibilité)
export const getDefaultSitelinkMetadata = () => {
  return getEssentialPagesMetadata().filter(page => 
    ['/devis', '/contact'].includes(page.page_path)
  );
};
